// @flow

import React, {useEffect, useMemo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';

//$FlowFixMe
import dayJsUtils from '@date-io/dayjs';
import CustomDatePicker from '../../../../marketing/coupons/components/CustomDatePicker/CustomDatePicker';

import {mapNumberToDay} from '../../utils';

import {useAdditionalInformationContext} from '../../context/hooks';
import {Typography} from '@material-ui/core';

import type {CheckoutInput, Order} from '../../../../../entities';
import type {MaterialUiPickersDate} from '@material-ui/pickers/typings/date';

type Props = {
  item: CheckoutInput,
  classes: {[key: string]: any},
  order?: Order,
};

const useStyles = makeStyles(theme => ({
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
}));

const formatDateToRequest = (date: Date) => {
  const year = date
    .getFullYear()
    .toString()
    .padStart(4, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date
    .getDate()
    .toString()
    .padStart(2, '0');

  return `${year}-${month}-${day}`;
};

function parseStringToDate(dateString: string) {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export default function CalendarInput(props: Props) {
  const {item, classes: propClasses, order} = props;
  const {schema} = item;

  const classes = useStyles();

  const {
    additionalInformation,
    setAdditionalInformation,
    additionalIformationValidate,
  } = useAdditionalInformationContext();

  const allowedDays = schema?.allowed_weekdays ?? [];

  const minDaysAfterSelect = !!schema?.enabled_min_days_to_select
    ? schema.min_days_to_select
    : 0;

  const selectedOptions: any = useMemo(
    () => additionalInformation[item.id]?.value ?? [],
    [additionalInformation, item.id]
  );

  const value = useMemo(() => {
    if (!!selectedOptions[0]) {
      return parseStringToDate(selectedOptions[0]);
    }
    return null;
  }, [selectedOptions]);

  const minDate = useMemo(() => {
    if (!isNaN(minDaysAfterSelect)) {
      const date = new Date();
      date.setDate(date.getDate() + minDaysAfterSelect);
      return date;
    }
  }, [minDaysAfterSelect]);

  const shouldDisableDate = (date: MaterialUiPickersDate) => {
    const dayOfWeek = new Date(date).getDay();
    const dayObj = mapNumberToDay[dayOfWeek.toString()];
    if (dayObj) {
      const name = dayObj.en_name;
      return !allowedDays.find(el => name.toUpperCase().startsWith(el));
    } else return false;
  };

  const withErrors = useMemo(() => {
    if (!item.required) return;
    if (selectedOptions.length === 0) return 'Campo obligatorio';
  }, [item.required, selectedOptions]);

  const handleDateChange = useCallback(
    (e: any) => {
      const date = e;
      if (date) {
        const newDate = new Date(date);
        setAdditionalInformation(prev => {
          const prevInputInfo = prev[item.id] ?? {};
          return {
            ...prev,
            [item.id]: {
              ...prevInputInfo,
              value: [formatDateToRequest(newDate)],
            },
          };
        });
      }
    },
    [item.id, setAdditionalInformation]
  );

  const orderAdditionalInfo = useMemo(() => {
    if (order && order.additionalInformation) {
      return order.additionalInformation.find(
        el => el.storeOrderAdditionalInformation.id === item.id
      );
    }
  }, [item, order]);

  useEffect(() => {
    setAdditionalInformation(prev => {
      const prevInputInfo = prev[item.id] ?? {};
      return {
        ...prev,
        [item.id]: {
          ...prevInputInfo,
          error: withErrors,
        },
      };
    });
  }, [item, setAdditionalInformation, withErrors]);

  useEffect(() => {
    if (orderAdditionalInfo) {
      const {additionalInformation} = orderAdditionalInfo;
      if (additionalInformation.value) {
        setAdditionalInformation(prev => {
          return {
            ...prev,
            [item.id]: {
              value: [
                formatDateToRequest(
                  parseStringToDate(
                    typeof additionalInformation.value === 'string'
                      ? additionalInformation.value
                      : ''
                  )
                ),
              ],
            },
          };
        });
      }
    }
  }, [item.id, orderAdditionalInfo, setAdditionalInformation]);

  return (
    <MuiPickersUtilsProvider utils={dayJsUtils}>
      <label className={classes.label}>
        <span>{item.description ?? item.name}</span>
        <CustomDatePicker
          minDate={minDate}
          value={value}
          shouldDisableDate={shouldDisableDate}
          handleChange={handleDateChange}
          error={!!additionalIformationValidate && withErrors}
        />
      </label>
      {withErrors && additionalIformationValidate && (
        <Typography className={propClasses.error}>{withErrors}</Typography>
      )}
    </MuiPickersUtilsProvider>
  );
}
